/**
 * Vanity-assembly-aware object matchers for the Design Center.
 *
 * The Design Center asks "is the room's vanity light here?" — a simple
 * `types.includes(obj.type)` check misses pendants stored as
 * `ceiling-light`, and conversely would over-match ambient ceiling
 * lights that aren't part of any vanity. The canonical predicate is:
 * the light shares a `fixtureGroupId` with a vanity object in the same
 * room.
 *
 * Mirrors the backend's `isAssemblyLight` in
 * `shared-lib/vanity-assembly/fixtureGroup.js` for the frontend (we
 * don't share code here — keep it light and self-contained).
 */

// Object types that, when paired with a vanity's fixtureGroupId, are
// considered the vanity's light. Matches the set the assembly can
// emit (sconce, overhead bar, ceiling pendant).
const VANITY_LIGHT_TYPES = new Set([
  'wall-light',
  'overhead-wall-light',
  'ceiling-light',
]);

// Object types that represent a vanity (the cabinet member of the
// assembly). Either one carries the assembly's `fixtureGroupId`.
const VANITY_PARENT_TYPES = new Set(['lower-cabinet', 'vanity']);

const isVanityParent = (obj) => obj && VANITY_PARENT_TYPES.has(obj.type);

const isVanityLightType = (obj) => obj && VANITY_LIGHT_TYPES.has(obj.type);

/**
 * Set of fixtureGroupIds owned by any vanity object in the room.
 * Lights must share one of these ids to count as the vanity's light.
 */
const collectVanityGroupIds = (objects = []) => {
  const ids = new Set();
  for (const obj of objects) {
    if (isVanityParent(obj) && obj.fixtureGroupId) {
      ids.add(obj.fixtureGroupId);
    }
  }
  return ids;
};

/**
 * True iff `obj` is a light that belongs to a vanity assembly in the
 * given room. Lights without a fixtureGroupId, or with one that no
 * vanity shares, are ambient room lights and return false.
 */
const isVanityAssemblyLight = (obj, allObjects = []) => {
  if (!isVanityLightType(obj)) return false;
  if (!obj.fixtureGroupId) return false;
  const vanityGroupIds = collectVanityGroupIds(allObjects);
  return vanityGroupIds.has(obj.fixtureGroupId);
};

/**
 * All vanity-assembly lights in the room. Used by the design center to
 * gather every light tied to any vanity (rare today, but the API stays
 * honest about plurality).
 */
const findVanityAssemblyLights = (objects = []) => {
  const vanityGroupIds = collectVanityGroupIds(objects);
  if (!vanityGroupIds.size) return [];
  return objects.filter(
    (obj) =>
      isVanityLightType(obj) &&
      obj.fixtureGroupId &&
      vanityGroupIds.has(obj.fixtureGroupId)
  );
};

/**
 * True iff the room contains a vanity. The Design Center uses this to
 * decide whether the Vanity Light category should appear (an empty
 * "+ Add" CTA still wants to show when a vanity exists but no light
 * does).
 */
const roomHasVanity = (objects = []) => objects.some(isVanityParent);

module.exports = {
  isVanityAssemblyLight,
  findVanityAssemblyLights,
  roomHasVanity,
};
